/**
 * @fileoverview Firm-local calendar dates.
 *
 * The product serves Australian insolvency firms, so "today" on any
 * generated document means the Sydney calendar day — never the runtime's
 * own clock. Convex functions execute in UTC, which sits 10–11 hours
 * behind Sydney: `new Date().toISOString().slice(0, 10)` is *yesterday*
 * for the entire Sydney morning, which is exactly when day-one letters
 * get generated (MOR-1031: a 26th appointment produced letters dated
 * the 25th).
 *
 * Browsers get the same treatment for consistency: the value a
 * practitioner previews client-side must match what a server-side fill
 * produces for the same field.
 */

export const SYDNEY_TZ = "Australia/Sydney";

/**
 * The Sydney calendar date of `now` as ISO `YYYY-MM-DD`.
 *
 * Falls back to the UTC date if the runtime lacks ICU timezone data —
 * wrong for Sydney mornings, but never throws mid–document-generation.
 *
 * @param now - Injectable clock, for tests.
 */
export function sydneyDateIso(now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-AU", {
      timeZone: SYDNEY_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const part = (type: string) => parts.find((p) => p.type === type)?.value;
    const year = part("year");
    const month = part("month");
    const day = part("day");
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    // Fall through to UTC.
  }
  return now.toISOString().slice(0, 10);
}
